import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShieldCheck, Eye, EyeOff, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';

const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&display=swap');
.cpl { --ink:#14232e; --ink-2:#4a5b68; --line:#dde3e8; --brand:#0e5a6b; --brand-d:#0a4552; --risk-bg:#fdebe5;
  font-family:'IBM Plex Sans',system-ui,sans-serif; color:var(--ink); min-height:100vh; display:grid; place-items:center; padding:24px; background:#0b3f4b; position:relative; box-sizing:border-box; }
.cpl *, .cpl *::before, .cpl *::after { box-sizing:border-box; }
.cpl button, .cpl input { font-family:inherit; }
.cpl-hazard { position:absolute; left:0; right:0; top:0; height:10px; background:repeating-linear-gradient(-45deg,#f2b705 0 14px,#14232e 14px 28px); }
.cpl-card { width:100%; max-width:400px; background:#fff; border-radius:14px; padding:36px 32px; box-shadow:0 16px 48px rgba(0,0,0,.28); }
.cpl-mark { width:46px; height:46px; border-radius:11px; display:grid; place-items:center; background:var(--brand); color:#fff; margin-bottom:20px; }
.cpl h1 { margin:0; font-size:24px; font-weight:700; letter-spacing:-.3px; }
.cpl-sub { margin:8px 0 24px; font-size:14px; line-height:1.55; color:var(--ink-2); }
.cpl-error { display:flex; gap:10px; align-items:flex-start; margin-bottom:16px; padding:12px 14px; border-radius:10px; background:var(--risk-bg); border:1px solid #f5c6b8; color:#8a2a0f; font-size:13px; line-height:1.45; }
.cpl-error svg { flex-shrink:0; margin-top:1px; }
.cpl form { display:grid; gap:16px; }
.cpl label { display:grid; gap:6px; font-size:12px; font-weight:500; color:var(--ink-2); }
.cpl-input { display:flex; align-items:center; height:42px; border:1px solid var(--line); border-radius:8px; background:#fff; }
.cpl-input:hover { border-color:#b7c2cb; }
.cpl-input:focus-within { border-color:var(--brand); box-shadow:0 0 0 3px rgba(14,90,107,.14); }
.cpl-input input { flex:1; min-width:0; height:100%; border:0; outline:0; background:transparent; padding:0 12px; font-size:14px; color:var(--ink); }
.cpl-eye { border:0; background:none; padding:0 12px; height:100%; cursor:pointer; color:var(--ink-2); display:grid; place-items:center; }
.cpl-eye:focus-visible, .cpl-submit:focus-visible, .cpl-back:focus-visible { outline:2px solid var(--brand); outline-offset:2px; border-radius:6px; }
.cpl-submit { height:44px; border:0; border-radius:8px; background:var(--brand); color:#fff; font-size:14px; font-weight:600; cursor:pointer; margin-top:4px; transition:background .15s; }
.cpl-submit:hover:not(:disabled) { background:var(--brand-d); }
.cpl-submit:disabled { opacity:.65; cursor:not-allowed; }
.cpl-back { display:block; margin-top:20px; text-align:center; font-size:13px; font-weight:500; color:var(--brand); text-decoration:none; }
.cpl-back:hover { text-decoration:underline; text-underline-offset:2px; }
`;

export default function CpanelLoginPage() {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    try {
      const { data } = await api.post('/cpanel/auth/login', { username: username.trim(), password });
      localStorage.setItem('hse_cpanel_token', data.token);
      navigate('/cpanel');
    } catch (err) {
      const msg = err.response?.data?.message || 'Control panel sign-in failed. Check your details and try again.';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cpl">
      <style>{STYLES}</style>
      <div className="cpl-hazard" aria-hidden="true" />
      <div className="cpl-card">
        <span className="cpl-mark"><ShieldCheck size={22} /></span>
        <h1>Control Panel</h1>
        <p className="cpl-sub">Sign in to manage administrator roles.</p>

        {error && <div className="cpl-error" role="alert"><AlertCircle size={17} /><span>{error}</span></div>}

        <form onSubmit={submit}>
          <label htmlFor="cpanel-username">
            Username
            <span className="cpl-input">
              <input id="cpanel-username" autoComplete="username" autoFocus value={username} onChange={(e) => setUsername(e.target.value)} required />
            </span>
          </label>
          <label htmlFor="cpanel-password">
            Password
            <span className="cpl-input">
              <input id="cpanel-password" type={showPw ? 'text' : 'password'} autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
              <button type="button" className="cpl-eye" onClick={() => setShowPw((s) => !s)} aria-label={showPw ? 'Hide password' : 'Show password'}>
                {showPw ? <EyeOff size={17} /> : <Eye size={17} />}
              </button>
            </span>
          </label>
          <button className="cpl-submit" type="submit" disabled={loading}>{loading ? 'Signing in…' : 'Sign in'}</button>
        </form>

        <Link className="cpl-back" to="/login">Back to Microsoft admin sign-in</Link>
      </div>
    </div>
  );
}